import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { between, Device, from, until } from '@helpers/media';
import { h4, paragraphRegular, paragraphSmall } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Global/Buttons/Button/Button.styles';
import TabButtonStyles from '@stories/Components/Global/Buttons/TabButton/TabButton.styles';
import DatePickerStyles from '@stories/Components/Global/DatePicker/DatePicker.styles';
import FormStyles from '@stories/Components/Global/Forms/Form.styles';
import TextboxStyles from '@stories/Components/Global/Inputs/Textbox/Textbox.styles';
import { ModalContentContainer, ModalContentInner } from '@stories/Components/Global/Modal/Modal.styles';
import QuantitySelectorStyles from '@stories/Components/Global/QuantitySelector/QuantitySelector.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.section `
  --priceColour: ${brand.global.red};

  &[data-site='ess'] {
    --disclaimerColour: ${brand.ess.secondary.darkGrey};
  }

  &[data-site='mep'] {
    --disclaimerColour: ${brand.mep.secondary.darkestGrey};
  }
`;
const AccountPromptWrapper = styled.div `
  overflow: hidden;
  margin: 0 0 ${fluid(20, 30)};
`;
const AccountPrompt = styled.div `
  background-color: var(--bgColour);
  color: var(--fgColour);
  padding: 20px;

  ${Container}[data-site='ess'] & {
    --bgColour: ${brand.global.white};
    --borderColour: ${brand.global.red};
    --fgColour: ${brand.ess.primary};

    position: relative;
    border: 2px solid var(--borderColour);

    &::after {
      content: '';
      display: block;
      position: absolute;
      bottom: -1px;
      right: -1px;
      height: ${fluid(36, 64)};
      width: ${fluid(36, 64)};
      transform: translate(50%, 50%) rotate(45deg);
      border-left: 2px solid var(--borderColour);
      background-color: var(--bgColour);
    }
  }

  ${Container}[data-site='mep'] & {
    --bgColour: ${brand.global.red};
    --fgColour: ${brand.global.white};

    border-radius: 8px;
  }
`;
const AccountTitle = styled.h3 `
  ${h4};

  margin: 0 0 ${fluid(9, 9)};

  ${Container}[data-site='mep'] & {
    ${fonts.museoSlab.medium};
  }
`;
const Label = styled.h3 `
  ${fonts.daxWide.bold};

  font-size: ${fluid(16, 18)};
  line-height: ${fluid(16, 26)};
  margin: 0 0 ${fluid(10, 13)};
  text-transform: uppercase;
`;
const AccountContent = styled.p `
  ${paragraphRegular};

  margin: 0;
`;
const AccountCtaList = styled.ul `
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 0 0;
  flex-wrap: wrap;

  @media ${from(Device.Tablet)} {
    ${Container}[data-logged-in='true'] & {
      gap: 12px;
    }
  }

  @media ${until(Device.TabletLarge)} {
    ${Label} {
      display: none;
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-logged-in='false'][data-site='mep'] & {
      flex-direction: row;
      align-items: center;
    }
  }

  @media ${between(Device.TabletLarge, Device.ActualDesktop)} {
    ${Container}[data-logged-in='false'] & {
      gap: ${fluid(16, 18, Device.DesktopLarge)};

      ${ButtonStyles.Container}[data-type="secondary"] {
        padding-left: 8px;
        padding-right: 8px;
      }

      ${ButtonStyles.Text} {
        font-size: ${fluid(16, 18, Device.DesktopLarge)};
      }
    }
  }
`;
const SelectorWrapper = styled.div `
  background-color: var(--bgColour);
  padding: ${fluid(20, 20)};
  display: flex;
  flex-direction: column;

  ${Container}[data-site='ess'] & {
    --bgColour: ${brand.global.white};
  }

  ${Container}[data-site='mep'] & {
    --bgColour: ${brand.global.background};

    border-radius: 8px;
  }

  > ${ButtonStyles.Container} {
    margin: auto 0 0;
  }

  /* Adjust button width to compensate for hover state */
  ${Container}[data-site='ess'] & {
    @media ${until(Device.TabletLarge)} {
      ${ButtonStyles.Container}[data-type='secondary'] {
        width: calc(100% - 30px);

        ${ButtonStyles.Text} {
          transform: translateX(15px);
        }
      }
    }

    @media ${from(Device.TabletLarge)} {
      ${ButtonStyles.Container}[data-type='secondary'] {
        margin: 0 auto;
        width: calc(100% - 60px);
      }
    }
  }
`;
const ProductTabs = styled.div `
  display: flex;

  ${TabButtonStyles.Container} {
    --borderWidth: 0px;
    --colourText: ${brand.global.white};
    --colourBg: ${brand.ess.secondary.midGrey};
    --colourBgActive: ${brand.global.red};
    --colourBgHover: ${brand.global.red};

    flex: 1 1 50%;
    justify-content: center;

    ${Container}[data-site='mep'] & {
      border-radius: 8px 8px 0 0;
    }
  }
`;
const PriceQtyWrapper = styled.div `
  display: flex;
  column-gap: ${fluid(12, 24)};
  justify-content: space-between;
  margin: 0 0 ${fluid(16, 36)};
  flex-wrap: wrap;

  ${QuantitySelectorStyles.Container} {
    margin-top: auto;
  }

  > span {
    flex: 1 1 0;
  }
`;
const PriceWrapper = styled.div `
  margin: 0 0 10px;
  color: var(--priceColour);
`;
const Price = styled.span `
  ${fonts.daxWide.bold};

  font-size: ${fluid(20, 32)};
  line-height: ${fluid(28, 42)};

  ${Container}[data-site='mep'] & {
    ${fonts.museoSlab.bold};
  }
`;
const Suffix = styled.span `
  ${fonts.daxWide.regular};

  font-size: ${fluid(12, 14)};
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;
const VatText = styled.p `
  ${paragraphSmall};

  color: var(--disclaimerColour);
  margin: 0 0 4px;

  &:last-child {
    margin: 0;
  }
`;
const DateWaiverWrapper = styled.div `
  margin: 0 0 ${fluid(20, 26)};

  ${FormStyles.Row} {
    margin-bottom: 16px;
  }

  ${FormStyles.Field} {
    position: relative;
  }

  ${TextboxStyles.Input} {
    border: 0;
    height: 46px;
    line-height: 46px;

    &:hover {
      cursor: pointer;
    }

    ${Container}[data-site= 'ess'] & {
      border-radius: 0;
      background-color: ${rgba(brand.ess.secondary.midGrey, 0.1)};
    }

    @media ${between(Device.Desktop, Device.ActualDesktop)} {
      font-size: 16px;
    }
  }
`;
const CalendarIcon = styled.span `
  pointer-events: none;
  display: block;
  position: absolute;
  right: 12px;
  bottom: 14px;

  > svg {
    display: block;
    width: 18px;
  }

  ${Container}[data-site='ess'] & {
    color: ${brand.ess.primary};
  }

  ${Container}[data-site='mep'] & {
    color: ${brand.mep.primary};
  }
`;
const Waiver = styled.p `
  color: var(--priceColour);
  font-size: ${fluid(12, 14)};
  line-height: 20px;
  margin: 0 0 8px;

  ${Container}[data-site='ess'] & {
    ${fonts.daxWide.medium};
  }

  ${Container}[data-site='mep'] & {
    ${fonts.museoSlab.medium};
  }
`;
const ScrollLinkContainer = styled.div `
  border-top: 1px solid var(--borderColour);
  margin: 20px 0 0;
  padding: 20px 0 0;

  ${Container}[data-site='ess'] & {
    --borderColour: ${brand.ess.secondary.darkestGrey};
  }

  ${Container}[data-site='mep'] & {
    --borderColour: ${brand.mep.primary};
  }
`;
const AddToBasketModal = styled(ModalContentContainer) ``;
const AddToBasketModalContent = styled(ModalContentInner) `
  --priceColour: ${brand.global.red};

  ${FormStyles.Row},
  ${DatePickerStyles.Wrapper} {
    margin: 0 0 ${fluid(20, 40)};
  }

  /* Adjust button width to compensate for active state */

  ${Container}[data-site='ess'] & {
    @media ${until(Device.TabletLarge)} {
      ${ButtonStyles.Container} {
        width: calc(100% - 30px);
      }

      ${ButtonStyles.Text} {
        transform: translateX(15px);
      }
    }
  }

  ${QuantitySelectorStyles.Container} {
    [data-site='mep'] & {
      --bgColour: ${brand.mep.secondary.lightestGrey};
    }
  }
`;
const ModalButtonWrapper = styled.div `
  text-align: center;
`;
const LabelWrapper = styled.div `
  display: flex;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;
const Sku = styled.span `
  ${fonts.daxWide.regular};

  color: currentColor;
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(16, 26)};

  &[data-bottom-spacing] {
    margin: -3px 0 ${fluid(10, 13)};
  }
`;
const HireSaleSelectorStyles = {
    AccountContent,
    AccountCtaList,
    AccountPrompt,
    AccountPromptWrapper,
    AccountTitle,
    AddToBasketModal,
    AddToBasketModalContent,
    CalendarIcon,
    Container,
    DateWaiverWrapper,
    Label,
    ModalButtonWrapper,
    Price,
    PriceQtyWrapper,
    PriceWrapper,
    ProductTabs,
    ScrollLinkContainer,
    SelectorWrapper,
    Suffix,
    VatText,
    Waiver,
    LabelWrapper,
    Sku,
};
export default HireSaleSelectorStyles;
